import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const mode = useSelector((state) => state.theme.mode);
    const Icon = item.icon;
    const isNew = item.isNew || false;

    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size="1.3rem" style={{ color: mode === 'dark' ? 'gold' : '' }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6,
                height: customization.isOpen.findIndex((id) => id === item?.id) > -1 ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id });
        //sidemenu closed when clicked on any item during mobile view
        if (matchesSM) dispatch({ type: SET_MENU, opened: true });
    };

    // active menu item on page load
    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
        // eslint-disable-next-line
    }, []);
    const isSelected = customization.isOpen.findIndex((id) => id === item.id) > -1;
    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: `${customization.borderRadius}px`,
                mb: 0.5,
                alignItems: 'flex-start',
                backgroundColor: isSelected ? '#1976d2' : level > 1 ? 'transparent !important' : 'inherit',
                py: level > 1 ? 1 : 1.25,
                pl: `${level * 24}px`,
                '&:hover': {
                    backgroundColor: mode === 'dark' ? '#1f1f1f' : ''
                }
            }}
            selected={customization.isOpen.findIndex((id) => id === item.id) > -1}
            onClick={() => itemHandler(item.id)}
        >
            <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>{itemIcon}</ListItemIcon>
            <ListItemText
                primary={
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                            variant={customization.isOpen.findIndex((id) => id === item.id) > -1 ? 'h5' : 'body1'}
                            sx={{
                                background:
                                    mode === 'dark' && isSelected
                                        ? 'linear-gradient(to bottom, #1f1f1f, #114568, #0c507d, #0071bc)'
                                        : mode === 'dark'
                                        ? '#fff'
                                        : '#000',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            {item.title}
                        </Typography>
                        {isNew && (
                            <motion.div
                                initial={{ rotate: -5, scale: 0.8, opacity: 0 }}
                                animate={{ rotate: [-5, 5, -2, 2, 0], scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 1,
                                    ease: 'easeOut',
                                    type: 'spring',
                                    stiffness: 150,
                                    damping: 10
                                }}
                                style={{ display: 'inline-block' }}
                            >
                                <Chip
                                    label="New"
                                    size="small"
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.6px',
                                        textTransform: 'uppercase',
                                        borderRadius: '999px',
                                        px: 1.6,
                                        height: '22px',
                                        color: '#ffd700',
                                        background: 'linear-gradient(120deg, #034285ff, #191fc2ff, #2107c9ff)',
                                        backgroundSize: '200% 200%',
                                        animation: 'shimmer 3s ease-in-out infinite, heartbeat 1.5s infinite ease-in-out',
                                        boxShadow: '0 4px 12px rgba(0, 26, 54, 0.4)',
                                        '& .MuiChip-label': {
                                            padding: 0
                                        },
                                        '@keyframes shimmer': {
                                            '0%': { backgroundPosition: '0% 50%' },
                                            '50%': { backgroundPosition: '100% 50%' },
                                            '100%': { backgroundPosition: '0% 50%' }
                                        },
                                        '@keyframes heartbeat': {
                                            '0%, 100%': {
                                                transform: 'scale(1)'
                                            },
                                            '14%': {
                                                transform: 'scale(1.15)'
                                            },
                                            '28%': {
                                                transform: 'scale(1)'
                                            },
                                            '42%': {
                                                transform: 'scale(1.1)'
                                            },
                                            '70%': {
                                                transform: 'scale(1)'
                                            }
                                        }
                                    }}
                                />
                            </motion.div>
                        )}
                    </Box>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
